import { supabase } from "@/integrations/supabase/client";

export type ActiveOverlay = {
  id: string;
  title_ar: string | null;
  title_en: string | null;
  subtitle_ar: string | null;
  subtitle_en: string | null;
  cta_label_ar: string | null;
  cta_label_en: string | null;
  cta_href: string | null;
};

const OVERLAY_MARKETS = ["*", "SA", "AE", "KW", "QA", "BH", "OM", "IQ", "US"] as const;

function normalizeMarket(m: string | undefined | null) {
  if (!m) return "*" as const;
  return (OVERLAY_MARKETS as readonly string[]).includes(m) ? m : "*";
}

function pickOverlay(r: {
  id: string;
  title_ar: string | null;
  title_en: string | null;
  subtitle_ar: string | null;
  subtitle_en: string | null;
  cta_label_ar: string | null;
  cta_label_en: string | null;
  cta_href: string | null;
}) {
  return {
    id: r.id,
    title_ar: r.title_ar,
    title_en: r.title_en,
    subtitle_ar: r.subtitle_ar,
    subtitle_en: r.subtitle_en,
    cta_label_ar: r.cta_label_ar,
    cta_label_en: r.cta_label_en,
    cta_href: r.cta_href,
  } satisfies ActiveOverlay;
}

export async function fetchPublicCollectionOverlays(handles: string[], market?: string | null) {
  if (handles.length === 0) return {} as Record<string, ActiveOverlay>;

  const normalizedMarket = normalizeMarket(market);
  const nowIso = new Date().toISOString();

  const { data: rows, error } = await supabase
    .from("collection_overlay_campaigns")
    .select(
      "id, collection_handle, market, priority, starts_at, ends_at, title_ar, title_en, subtitle_ar, subtitle_en, cta_label_ar, cta_label_en, cta_href",
    )
    .in("collection_handle", handles)
    .eq("enabled", true)
    .in("market", [normalizedMarket, "*"])
    .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
    .or(`ends_at.is.null,ends_at.gt.${nowIso}`);

  if (error || !rows) {
    if (error) throw new Error(error.message);
    return {} as Record<string, ActiveOverlay>;
  }

  const grouped: Record<string, typeof rows> = {};
  for (const row of rows) {
    const handle = row.collection_handle;
    if (!grouped[handle]) grouped[handle] = [] as unknown as typeof rows;
    grouped[handle].push(row);
  }

  const out: Record<string, ActiveOverlay> = {};
  for (const [handle, list] of Object.entries(grouped)) {
    const sorted = [...list].sort((a, b) => {
      if (a.market !== b.market) {
        if (a.market === normalizedMarket) return -1;
        if (b.market === normalizedMarket) return 1;
      }
      if (a.priority !== b.priority) return b.priority - a.priority;
      const at = a.starts_at ? new Date(a.starts_at).getTime() : 0;
      const bt = b.starts_at ? new Date(b.starts_at).getTime() : 0;
      return bt - at;
    });
    out[handle] = pickOverlay(sorted[0]);
  }

  return out;
}